<template>
  <div class="card mb-2">
    <div class="card-content p-4">
      <header class="is-size-7 mb-2 px-1">
        <span class="has-text-grey">
          <router-link
            v-tooltip="creatorFullName"
            class="mr-1"
            :to="{
              name: 'userDetail',
              params: { userUsername: creator },
            }"
          >
            @{{ creator }}
          </router-link>
          <span v-tooltip="`${createdAtFormatted}`">
            {{ createdAtHumanized }}
          </span>
        </span>
        <span
          v-if="isSelf && editable && !isReadonly"
          class="icon hoverable-icon trash-icon"
          @click="handleDelete"
        >
          <i class="fa-solid fa-trash" />
        </span>
      </header>
      <main class="note-content is-flex p-1" style="width: 100%">
        <span class="icon has-text-grey">
          <i class="fa-solid fa-quote-left" />
        </span>
        <!-- eslint-disable vue/no-v-html -->
        <div class="text" v-html="sanitizedHtml" />
        <!-- eslint-enable -->
      </main>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { mapGetters } from 'vuex'

import { Profile, render } from '@/utils/sanitize'

export default {
  props: {
    created: String,
    creator: String,
    editable: Boolean,
    id: String,
    text: String,
    isReadonly: Boolean,
  },
  emits: ['delete'],
  computed: {
    ...mapGetters('auth', ['isLoggedInUser', 'getUserByUsername']),
    // NoteList resolves every author of the list in one request; this component
    // only reads the cache and falls back to the raw username.
    creatorFullName() {
      return this.getUserByUsername(this.creator)?.fullName || this.creator
    },
    isSelf() {
      return this.isLoggedInUser({ username: this.creator })
    },
    createdAt() {
      if (!this.created) return ''
      return moment(this.created)
    },
    createdAtFormatted() {
      return this.createdAt.format('[at] HH:mm [on] YYYY-MM-DD')
    },
    createdAtHumanized() {
      const now = moment()
      return moment.duration(this.createdAt.diff(now)).humanize(true)
    },
    sanitizedHtml() {
      // Note text is plain text — escape then linkify (preserves literal `<>`).
      return render(this.text, Profile.TEXT)
    },
  },
  methods: {
    handleDelete() {
      this.$emit('delete', this.id)
    },
  },
}
</script>

<style lang="scss" scoped>
.card {
  header {
    display: inline-flex;
    align-items: center;
    width: 100%;
    border-bottom: solid 1px var(--color-border-default);

    & > .icon.hoverable-icon.trash-icon {
      opacity: 0.4;
      margin-left: auto;

      &:hover {
        opacity: 1;
        color: var(--danger);
      }
    }
  }

  .note-content {
    width: 100%;

    & > .icon {
      margin-right: 0.8rem;
    }

    & > .text {
      padding: 0 0.2rem;
      overflow-wrap: break-word;
      word-wrap: break-word;
      word-break: break-word;
      hyphens: auto;
      -webkit-hyphens: auto;
      -ms-hyphens: auto;

      &:deep(a) {
        word-break: break-all;
        overflow-wrap: anywhere;
      }
    }
  }
}
</style>
